import { array, boolean, mixed, number, object, string, StringSchema } from 'yup';
import { CustomPageType } from '@ViewModels';

const HEX_CODE_REGEX = /^#([a-f0-9]{8}|[a-f0-9]{6}|[a-f0-9]{3})$/i;
const URL_REGEX = /^https?:\/\/[(www\\.)?a-zA-Z0-9@:%._\\+~#=-]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/i;

const HEX_CODE_ERROR = 'Color codes must begin with # and be 3-8 characters long';
const URL_ERROR = 'URLs must have a valid domain and protocol prefix';

// Reusable schema for hex color fields.
const hexColorSchema = () => string().matches(HEX_CODE_REGEX, { excludeEmptyString: true, message: HEX_CODE_ERROR });

const baseNavLinkSchema = object().shape({
	label: string().when('destination', {
		is: (destination: string) => destination,
		then: (schema: StringSchema) => schema.required('Links must have a text label'),
	}),
	destination: string()
		.matches(URL_REGEX, { excludeEmptyString: true, message: URL_ERROR })
		.when('children', {
			is: (children: object[]) => children?.length > 0,
			then: (schema: StringSchema) => schema.max(0, 'Menu headers must not have a destination'),
		}),
});

export const navLinkSchema = baseNavLinkSchema.shape({
	children: array().of(baseNavLinkSchema).nullable(),
});

const blogFontSchema = object().shape({
	fontName: string(),
	fontUri: string().matches(URL_REGEX, { excludeEmptyString: true, message: URL_ERROR }),
});

const textAttributesSchema = object().shape({
	font: blogFontSchema,
	color: hexColorSchema(),
	backgroundColor: hexColorSchema(),
});

export const headerSchema = object().shape({
	type: mixed<CustomPageType>().oneOf([CustomPageType.Header]),
	backgroundColor: hexColorSchema(),
	dropdown: textAttributesSchema,
	hoverColor: hexColorSchema(),
	logoOnlyHeader: boolean(),
	menuLinks: array().of(navLinkSchema),
	overallFont: blogFontSchema,
	textColor: hexColorSchema(),
	mainPageTitle: string().max(100, 'Page title must be 100 characters or less'),
});

export const footerSchema = object().shape({
	type: mixed<CustomPageType>().oneOf([CustomPageType.Footer]),
	backgroundColor: hexColorSchema(),
	textColor: hexColorSchema(),
	linkColor: hexColorSchema(),
	text: object().shape({
		document: string(),
		documentVersion: number(),
		source: string(),
	}),
	copyrightInfo: string().nullable(),
	links: array().of(navLinkSchema),
});

const customTitleTextSchema = textAttributesSchema.shape({
	title: string(),
});

const pageButtonSchema = textAttributesSchema.shape({
	hoverColor: hexColorSchema(),
});

const blogCardSchema = object().shape({
	titleStyle: textAttributesSchema,
	linkStyle: textAttributesSchema,
	showAuthorMetaData: boolean(),
});

export const blogHomeSchema = object().shape({
	type: mixed<CustomPageType>().oneOf([CustomPageType.BlogHome]),
	pageBackgroundColor: hexColorSchema(),
	headerImage: object().nullable(),
	hideNook: boolean(),
	font: blogFontSchema,
	blogTitle: customTitleTextSchema,
	cardStyle: blogCardSchema,
});

export const blogPostSchema = object().shape({
	type: mixed<CustomPageType>().oneOf([CustomPageType.BlogPost]),
	blogTitle: textAttributesSchema,
	blogHeadlines: textAttributesSchema,
	blogBody: textAttributesSchema,
	showMoreButton: pageButtonSchema,
});
